import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState, useMemo } from "react";
import { Button, Modal, Form, Spin, Input, Checkbox, Radio, Divider, App } from "antd";
import { request, _i, __ } from "../../utils";
import { HeartTwoTone } from "@ant-design/icons";
import { locationRestPluginFeedbackPost } from "../../wp-api";
const FEEDBACK_FORM_LAYOUT = {
    labelCol: {
        span: 24
    },
    wrapperCol: {
        span: 24
    }
};
const FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM = {
    marginBottom: 8
};
const FeedbackModal = (param)=>{
    let { initialValues = {}, plugin, name, hasAtLeastOneActiveLicense, isPluginActiveForNetwork, privacyPolicy, privacyProvider, onClose, onDeactivate } = param;
    const { message } = App.useApp();
    const reasons = useMemo(()=>({
            "upgrade-to-pro": __("Upgrade to PRO Version"),
            "not-working": __("Plugin does not work"),
            "missing-features": __("Not the features I want"),
            incompatible: __("Incompatible with themes/plugins"),
            "missing-doc": __("Lack of documentation"),
            "found-better-plugin": __("Found a better plugin"),
            temp: __("Temporary deactivation"),
            other: __("Other")
        }), []);
    const [form] = Form.useForm();
    const formName = `license-form-${plugin}`;
    const [visible, setVisible] = useState(true);
    const [busy, setBusy] = useState(false);
    const handleClose = useCallback(()=>{
        if (window.confirm(__("Are you sure you want to leave the feedback form?"))) {
            setVisible(false);
        }
    }, []);
    const handleFinish = useCallback(async (param)=>{
        let { skip = false, reason = "other", note = "", email = "", name = "", deactivateLicense = false } = param;
        try {
            setBusy(true);
            await request({
                location: locationRestPluginFeedbackPost,
                params: {
                    slug: plugin
                },
                request: {
                    skip,
                    reason,
                    note,
                    email,
                    name: email ? name : "",
                    deactivateLicense
                }
            });
            onDeactivate == null ? void 0 : onDeactivate();
        } catch (e) {
            var _responseJSON_data_body_, _responseJSON_data_body, _responseJSON_data;
            const { responseJSON } = e;
            const code = responseJSON == null ? void 0 : (_responseJSON_data = responseJSON.data) == null ? void 0 : (_responseJSON_data_body = _responseJSON_data.body) == null ? void 0 : (_responseJSON_data_body_ = _responseJSON_data_body[0]) == null ? void 0 : _responseJSON_data_body_.code;
            // Ignore some errors
            if ([
                "DeactivationFeedbackAlreadyGiven",
                "DeactivationFeedbackMightBeSpam"
            ].indexOf(code) > -1 || !code) {
                onDeactivate == null ? void 0 : onDeactivate();
                return;
            } else {
                var _responseJSON_data_body_1, _responseJSON_data_body1, _responseJSON_data1;
                message.error(responseJSON == null ? void 0 : (_responseJSON_data1 = responseJSON.data) == null ? void 0 : (_responseJSON_data_body1 = _responseJSON_data1.body) == null ? void 0 : (_responseJSON_data_body_1 = _responseJSON_data_body1[0]) == null ? void 0 : _responseJSON_data_body_1.message);
            }
        } finally{
            setBusy(false);
        }
    }, [
        form,
        plugin
    ]);
    const handleSkip = useCallback(()=>{
        const deactivateLicense = form.getFieldValue("deactivateLicense");
        if (deactivateLicense) {
            handleFinish({
                skip: true,
                deactivateLicense
            });
        } else {
            onDeactivate == null ? void 0 : onDeactivate();
        }
    }, [
        handleFinish
    ]);
    return /*#__PURE__*/ _jsx(Modal, {
        afterClose: onClose,
        onCancel: handleClose,
        open: visible,
        footer: [
            /*#__PURE__*/ _jsx(Button, {
                type: "default",
                onClick: handleSkip,
                className: "alignleft",
                disabled: busy,
                children: /*#__PURE__*/ _jsx("b", {
                    children: __("Skip & Deactivate")
                })
            }, "skip"),
            /*#__PURE__*/ _jsx(Button, {
                type: "primary",
                htmlType: "submit",
                form: formName,
                disabled: busy,
                children: __("Deactivate")
            }, "submit")
        ],
        title: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(HeartTwoTone, {
                    twoToneColor: "#eb2f96"
                }),
                " ",
                __("Too bad you are leaving")
            ]
        }),
        children: /*#__PURE__*/ _jsx(Spin, {
            spinning: busy,
            children: /*#__PURE__*/ _jsxs(Form, {
                name: formName,
                id: formName,
                form: form,
                ...FEEDBACK_FORM_LAYOUT,
                onFinish: handleFinish,
                initialValues: initialValues,
                layout: "vertical",
                labelWrap: true,
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "reason",
                        label: /*#__PURE__*/ _jsx(_Fragment, {
                            children: __("Please give us feedback why you deactivate %s.", name)
                        }),
                        style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
                        required: true,
                        rules: [
                            {
                                required: true,
                                message: __("Please provide a reason!")
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Radio.Group, {
                            children: Object.keys(reasons).map((choice)=>/*#__PURE__*/ _jsx(Radio, {
                                    value: choice,
                                    style: {
                                        width: "calc(50% - 8px)",
                                        float: "left"
                                    },
                                    children: reasons[choice]
                                }, choice))
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prevValues, nextValues)=>prevValues.reason !== nextValues.reason,
                        children: (param)=>{
                            let { getFieldValue } = param;
                            return !!getFieldValue("reason") && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        label: __("What could we do better?"),
                                        name: "note",
                                        style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
                                        children: /*#__PURE__*/ _jsx(Input.TextArea, {
                                            autoSize: {
                                                minRows: 3
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        noStyle: true,
                                        shouldUpdate: (prevValues, nextValues)=>prevValues.answerTerms !== nextValues.answerTerms,
                                        children: (param)=>{
                                            let { getFieldValue } = param;
                                            return !!getFieldValue("reason") && /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(Form.Item, {
                                                        name: "email",
                                                        label: __("Email for answer/solution"),
                                                        style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
                                                        rules: [
                                                            {
                                                                type: "email",
                                                                required: getFieldValue("answerTerms"),
                                                                message: __("Please provide a valid e-mail address!")
                                                            }
                                                        ],
                                                        children: /*#__PURE__*/ _jsx(Input, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(Form.Item, {
                                                        noStyle: true,
                                                        shouldUpdate: (prevValues, nextValues)=>prevValues.email !== nextValues.email,
                                                        children: (param)=>{
                                                            let { getFieldValue } = param;
                                                            return /*#__PURE__*/ _jsxs(_Fragment, {
                                                                children: [
                                                                    !!getFieldValue("email") && /*#__PURE__*/ _jsx(Form.Item, {
                                                                        name: "name",
                                                                        label: __("Name"),
                                                                        required: true,
                                                                        style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
                                                                        rules: [
                                                                            {
                                                                                required: true,
                                                                                message: __("Please provide a name!")
                                                                            }
                                                                        ],
                                                                        children: /*#__PURE__*/ _jsx(Input, {})
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(Form.Item, {
                                                                        name: "answerTerms",
                                                                        valuePropName: "checked",
                                                                        required: true,
                                                                        rules: [
                                                                            {
                                                                                type: "boolean",
                                                                                required: !!getFieldValue("email"),
                                                                                transform: (value)=>value || undefined,
                                                                                message: __("Please confirm that you have checked the privacy policy.")
                                                                            }
                                                                        ],
                                                                        style: FEEDBACK_FORM_LAYOUT_MARGIN_BOTTOM,
                                                                        children: /*#__PURE__*/ _jsx(Checkbox, {
                                                                            style: {
                                                                                zoom: 0.8
                                                                            },
                                                                            children: _i(__("I would like to receive a response to my request. For this purpose, I agree to the data processing of my feedback and my e-mail address. I have read and acknowledge the %s {{a}}Privacy Policy{{/a}}.", privacyProvider), {
                                                                                a: /*#__PURE__*/ _jsx("a", {
                                                                                    href: privacyPolicy,
                                                                                    target: "_blank",
                                                                                    rel: "noreferrer"
                                                                                })
                                                                            })
                                                                        })
                                                                    })
                                                                ]
                                                            });
                                                        }
                                                    })
                                                ]
                                            });
                                        }
                                    })
                                ]
                            });
                        }
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        shouldUpdate: (prevValues, nextValues)=>prevValues.note !== nextValues.note || prevValues.answerTerms !== nextValues.answerTerms,
                        children: (param)=>{
                            let { getFieldValue } = param;
                            const answerTerms = getFieldValue("answerTerms") || false;
                            const note = getFieldValue("note") || "";
                            if (answerTerms) {
                                return null;
                            }
                            return note.split(" ").length >= 5 ? /*#__PURE__*/ _jsx("div", {
                                className: "notice notice-info inline below-h2 notice-alt",
                                style: {
                                    margin: 0
                                },
                                children: /*#__PURE__*/ _jsx("p", {
                                    children: __("Allow us to reply to you by email and we will get back to you as soon as possible!")
                                })
                            }) : /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                style: {
                                    marginTop: 5
                                },
                                children: _i(__("Are there any problems with the setup or use of the plugin? Maybe we can help you in the support. {{a}}Contact support{{/a}}."), {
                                    a: /*#__PURE__*/ _jsx("a", {
                                        href: __("https://devowl.io/support/"),
                                        target: "_blank",
                                        rel: "noreferrer"
                                    })
                                })
                            });
                        }
                    }),
                    hasAtLeastOneActiveLicense && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Divider, {
                                style: {
                                    margin: "12px 0"
                                }
                            }),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                style: {
                                    marginBottom: 0
                                },
                                name: "deactivateLicense",
                                valuePropName: "checked",
                                label: __("Do you want to deactivate your active license so that you can use it again on another site?"),
                                children: /*#__PURE__*/ _jsx(Checkbox, {
                                    children: isPluginActiveForNetwork ? __("Yes, deactivate all active licenses for all sites within this multisite") : __("Yes, deactivate license")
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
};
export { FeedbackModal };
